import React, {FC, ReactNode} from 'react';
import {Text} from 'components-lib';
import {FastField as FormikFastField, Field as FormikField, FieldProps} from 'formik';
import {IComponentWithChildren, IOption} from 'models';

interface IFieldProps<T> extends IComponentWithChildren {
  name: string;
  loading?: boolean;
  value?: string | number | Date;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  toolbarHidden?: boolean;
  checked?: boolean;
  Component: FC<any>;
  options?: IOption[];
  multiple?: boolean;
  withMinDate?: boolean;
  withMaxDate?: boolean;
  maxWidth?: boolean;
  minDateFormField?: string;
  maxDateFormField?: string;
  minDateMessage?: string;
  maxDateMessage?: string;
  withDefaultChangeHandler?: boolean;
  withEdit?: boolean;
  withIcon?: boolean;
  customValueChangeHandler?: T;
  debounceDelay?: number;
  withInstructionalText?: boolean;
  noOptionsText?: string;
  type?: string;
  min?: string;
  max?: string;
  withCheckboxRenderOption?: boolean;
  withSearchOnType?: boolean;
  withFontWeightBold?: boolean;
  disableCloseOnSelect?: boolean;
  allowDuplicateSelections?: boolean;
  color?: 'primary' | 'secondary';
  iconPosition?: string;
  icon?: ReactNode;
  rows?: number;
  disableFuture?: boolean;
  disablePast?: boolean;
  format?: string;
  isFilter?: boolean;
  open?: string | boolean;
  submitDisabled?: boolean;
  filterOptions?: (options: IOption[]) => IOption[];
  groupBy?: (option: IOption) => string;
  autoFocus?: boolean;
  inputChangeHandler?: (value: string) => void;
  onOpen?: (value: boolean) => void;
  digitsOnly?: boolean;
  allowDecimal?: boolean;
  /** When true, skip Text.Error here — the Component owns the single error message. */
  suppressFieldError?: boolean;
}

function renderFieldError(error: unknown) {
  if (typeof error === 'string' && error) {
    return <Text.Error>{error}</Text.Error>;
  }
  if (Array.isArray(error)) {
    const message = error.find((entry): entry is string => typeof entry === 'string' && !!entry);
    return message ? <Text.Error>{message}</Text.Error> : null;
  }
  return null;
}

export function Field<T = any>({name, Component, children, suppressFieldError = false, ...rest}: IFieldProps<T>) {
  return (
    <FormikField id={name} name={name}>
      {(props: FieldProps) => {
        const error: any = props.form.errors[name];
        return (
          <>
            <Component name={name} onBlur={props.field.onBlur} {...props} {...rest}>
              {children}
            </Component>
            {!suppressFieldError && renderFieldError(error)}
          </>
        );
      }}
    </FormikField>
  );
}

export function FastField<T = any>({name, Component, children, suppressFieldError = false, ...rest}: IFieldProps<T>) {
  return (
    <FormikFastField id={name} name={name}>
      {(props: FieldProps) => {
        const error: any = props.form.errors[name];
        return (
          <>
            <Component name={name} onBlur={props.field.onBlur} {...props} {...rest}>
              {children}
            </Component>
            {!suppressFieldError && renderFieldError(error)}
          </>
        );
      }}
    </FormikFastField>
  );
}
